import angular, { IHttpService } from 'angular'

/**
 * Resource for the balances API
 */
angular
  .module('webregister.factories')
  .factory('BalancesResource', BalancesResourceFactory)

export type BalancesResource = ReturnType<typeof BalancesResourceFactory>

function BalancesResourceFactory($http: IHttpService) {
  const BALANCES_ENDPOINT = '/api/2.0/balances'

  /**
   * Enable the Balances management for the current retailer. This is used solely to enable or disable gift cards.
   * @todo once the new gift cards reskin has been deployed this method should be renamed to something more appropriate.
   *
   * @method createRetailerSettings
   *
   * @param {Object} [data]
   *        The payload to pass.
   *
   *        @param {String} [data.status]
   *               Either 'ACTIVE' or 'INACTIVE'.
   *
   * @return {Promise} The $http request promise
   */
  function createRetailerSettings(data: $TSFixMe) {
    return $http
      .put(`${BALANCES_ENDPOINT}/settings`, data)
      .then(response => response.data)
  }

  /**
   * Get the gift card entity for the specified number
   *
   * @method getGiftCard
   * @param  number {String}
   *         The gift card number
   *
   * @return {Promise} The $http request promise
   */
  function getGiftCard(number: string) {
    return $http
      .get(`${BALANCES_ENDPOINT}/gift_cards/${number}`)
      .then(response => response.data)
  }

  /**
   * Get the gift card entity for the specified transaction ID
   *
   * @method getGiftCardByTransactionID
   * @param  transactionID {String}
   *         The gift card number
   *
   * @return {Promise} The $http request promise
   */
  function getGiftCardByTransactionID(transactionID: string) {
    return $http
      .get(`${BALANCES_ENDPOINT}/gift_cards/transactions/${transactionID}`)
      .then(response => response.data)
  }

  /**
   * Get the retailer gift card settings
   *
   * @method getRetailerGiftCardSettings
   *
   * @return {Promise} The $http request promise
   */
  function getRetailerGiftCardSettings() {
    return $http
      .get(`${BALANCES_ENDPOINT}/gift_cards/retailer/status`)
      .then(response => response.data)
  }

  /**
   * Gets the current report of retailer's gift cards
   *
   * @method getGiftCardsReport
   * @return {Promise} The $http request promise
   */
  function getGiftCardsReport() {
    return $http
      .get(`${BALANCES_ENDPOINT}/gift_cards/report`)
      .then(response => response.data)
  }

  /**
   * Post a new transaction on a gift card
   *
   * @method postGiftCardTransaction
   * @param  number {String}
   *         The gift card number
   *
   * @param  data {Object}
   *         The data to post
   *
   *   @param data.type {String}
   *          The type of transaction, e.g. REDEEMING
   *
   *   @param data.amount {Number}
   *          The amount of the transaction
   *
   * @return {Promise} The $http request promise
   */
  function postGiftCardTransaction(number: string, data: $TSFixMe) {
    return $http
      .post(`${BALANCES_ENDPOINT}/gift_cards/${number}/transactions`, data)
      .then(response => response.data)
  }

  /**
   * Import a csv of existing gift cards and balances for retailer
   *
   * @method postGiftCardCSV
   * @param  data {Object}
   *         The form data to post
   *
   * @return {Promise} The $http request promise
   */
  function postGiftCardCsv(data: $TSFixMe) {
    return $http
      .post(`${BALANCES_ENDPOINT}/gift_cards/csv_import`, data, {
        // Hacky options to allow us to post multi-part form data, see: http://stackoverflow.com/a/17504429/5179940
        withCredentials: true,
        headers: { 'Content-Type': undefined },
        transformRequest: angular.identity,
      })
      .then(response => response.data)
  }

  return {
    createRetailerSettings,
    getGiftCard,
    getGiftCardByTransactionID,
    getRetailerGiftCardSettings,
    getGiftCardsReport,
    postGiftCardTransaction,
    postGiftCardCsv,
  }
}
